import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import TopBar from "@/components/TopBar";
import ImageUpload from "@/components/ImageUpload";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const empty = {
  title: "",
  host_names: "",
  event_type: "mariage",
  event_date: "",
  event_time: "18:00",
  venue: "",
  address: "",
  description: "",
  cover_image_url: "",
  cover_image_public_id: "",
  home_delivery: true,
  subscription_method: "mpesa",
};

const SUBSCRIPTION_METHODS = [
  { id: "mpesa",      label: "M-Pesa" },
  { id: "orange",     label: "Orange Money" },
  { id: "airtel",     label: "Airtel Money" },
  { id: "illicocash", label: "Illico Cash" },
  { id: "cash",       label: "Cash" },
  { id: "card",       label: "Carte bancaire" },
];

export default function AdminEventForm() {
  const { t } = useLanguage();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!isEdit) {
      api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/events/${id}`).then((r) => {
        const ev = r.data.event;
        setForm({
          title: ev.title || "",
          host_names: ev.host_names || "",
          event_type: ev.event_type || "mariage",
          event_date: ev.event_date || "",
          event_time: ev.event_time || "18:00",
          venue: ev.venue || "",
          address: ev.address || "",
          description: ev.description || "",
          cover_image_url: ev.cover_image_url || "",
          cover_image_public_id: ev.cover_image_public_id || "",
          home_delivery: ev.home_delivery ?? true,
        });
        if (ev.event_date) setDate(new Date(ev.event_date));
      });
    }
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title || !form.host_names || !form.event_date || !form.venue) {
      setError("Veuillez remplir les champs requis.");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/events/${id}`, form);
        navigate(`/admin/events/${id}`);
      } else {
        const { data } = await api.post("/admin/events", form);
        navigate(`/admin/events/${data.id}`);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-2xl mx-auto px-5 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate(isEdit ? `/admin/events/${id}` : "/admin")}
          data-testid="form-back-btn"
          className="gap-2 -ml-3 mt-2 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </Button>

        <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] mt-6">
          {isEdit ? t("admin.eventForm.update") : t("admin.newEvent")}
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <Field label={t("admin.eventForm.title")}>
            <Input
              data-testid="event-title-input"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder={t("admin.eventForm.titlePlaceholder")}
              className="h-12 rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <Field label={t("admin.eventForm.hostNames")}>
            <Input
              data-testid="event-hosts-input"
              value={form.host_names}
              onChange={(e) => setField("host_names", e.target.value)}
              placeholder={t("admin.eventForm.hostNamesPlaceholder")}
              className="h-12 rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <Field label={t("admin.eventForm.type")}>
            <Select value={form.event_type} onValueChange={(v) => setField("event_type", v)}>
              <SelectTrigger data-testid="event-type-select" className="h-12 rounded-xl border-[#F2E2EC]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mariage">{t("admin.eventForm.types.mariage")}</SelectItem>
                <SelectItem value="collation">{t("admin.eventForm.types.collation")}</SelectItem>
                <SelectItem value="anniversaire">{t("admin.eventForm.types.anniversaire")}</SelectItem>
                <SelectItem value="autre">{t("admin.eventForm.types.autre")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("admin.eventForm.date")}>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="event-date-trigger"
                    className={cn(
                      "h-12 w-full rounded-xl border border-[#F2E2EC] bg-white px-3 text-left flex items-center gap-2",
                      !form.event_date && "text-[#5C4664]"
                    )}
                  >
                    <CalendarIcon className="w-4 h-4 text-[#D81B8C]" />
                    {form.event_date ? format(new Date(form.event_date), "PP") : "Sélectionner…"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      if (d) setField("event_date", d.toISOString().slice(0, 10));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label={t("admin.eventForm.time")}>
              <Input
                data-testid="event-time-input"
                type="time"
                value={form.event_time}
                onChange={(e) => setField("event_time", e.target.value)}
                className="h-12 rounded-xl border-[#F2E2EC]"
              />
            </Field>
          </div>

          <Field label={t("admin.eventForm.venue")}>
            <Input
              data-testid="event-venue-input"
              value={form.venue}
              onChange={(e) => setField("venue", e.target.value)}
              placeholder={t("admin.eventForm.venuePlaceholder")}
              className="h-12 rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <Field label={t("admin.eventForm.address")}>
            <Input
              data-testid="event-address-input"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              className="h-12 rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <Field label={t("admin.eventForm.description")}>
            <Textarea
              data-testid="event-description-input"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <ImageUpload
            value={form.cover_image_url ? (form.cover_image_public_id ? { url: form.cover_image_url, public_id: form.cover_image_public_id } : form.cover_image_url) : null}
            onChange={(asset) => {
              setField("cover_image_url", asset?.url || "");
              setField("cover_image_public_id", asset?.public_id || "");
            }}
            folder="milkado/events/"
            label={t("admin.eventForm.coverImage")}
            testid="event-cover"
          />

          <label className="flex items-center justify-between p-4 rounded-xl bg-white border border-[#F2E2EC]">
            <span className="text-sm text-[#2A0E3C] pr-3">
              {t("admin.eventForm.homeDelivery")}
            </span>
            <Switch
              data-testid="event-delivery-switch"
              checked={form.home_delivery}
              onCheckedChange={(v) => setField("home_delivery", v)}
              className="data-[state=checked]:bg-[#D81B8C]"
            />
          </label>

          {/* Subscription payment — only at event creation AND when the
              feature is enabled in super-admin settings. */}
          {!isEdit && settings?.subscription_fee_enabled !== false && (
            <div
              data-testid="subscription-block"
              className="p-5 rounded-2xl bg-[#FFF5FA] border border-[#F2E2EC]"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-display font-black text-[#2A0E3C]">
                    {t("admin.eventForm.subscriptionTitle")}
                  </h3>
                  <p className="text-xs text-[#5C4664] mt-1">
                    {t("admin.eventForm.subscriptionSub")}
                  </p>
                </div>
                {settings && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">
                      {t("admin.eventForm.subscriptionAmount")}
                    </div>
                    <div className="font-display font-black text-2xl text-[#D81B8C]">
                      ${Number(settings.subscription_fee_amount).toFixed(0)}
                    </div>
                  </div>
                )}
              </div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
                {t("admin.eventForm.subscriptionMethod")}
              </Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SUBSCRIPTION_METHODS.map((m) => {
                  const active = form.subscription_method === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      data-testid={`sub-method-${m.id}`}
                      onClick={() => setField("subscription_method", m.id)}
                      className={`px-3 h-11 rounded-xl text-sm font-semibold border transition-all ${
                        active
                          ? "border-[#D81B8C] bg-[#FCE7F3] text-[#2A0E3C]"
                          : "bg-white border-[#F2E2EC] hover:border-[#D81B8C]/50 text-[#5C4664]"
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <p data-testid="event-form-error" className="text-sm text-[#D81B8C] font-medium">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="event-submit"
            className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold text-base"
          >
            {loading
              ? t("common.loading")
              : isEdit
              ? t("admin.eventForm.update")
              : t("admin.eventForm.create")}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
